import type { Employee } from '../types/employee';
import {
  createDefaultCompliance,
  createDefaultMfaProfile,
  createDefaultPortfolio,
  createDefaultDocuments,
  syncComplianceFromDocuments,
} from '../types/employee';
import { hashPin } from './pinService';
import { getActiveCentreId } from './tenant/centreStorage';

export const EMPLOYEE_STORAGE_KEY = 'garderie-employees';
export const EMPLOYEE_STORAGE_VERSION = 2;

function buildSeedEmployees(): Employee[] {
  const seeds = [
    {
      id: 'teacher-1',
      firstName: 'Emily',
      lastName: 'Smith',
      email: 'emily.smith@kindergarten.com',
      status: 'active',
      pinHash: hashPin('1111'),
      hireDate: '2020-09-01',
      mfa: createDefaultMfaProfile({
        qualification: 'qualified',
        echelonCode: 'E3',
        ancienneteHours: 1200,
        accumulatedHours: 320,
        baseRateDisplay: 24.5,
      }),
      compliance: createDefaultCompliance({
        policeCheck: 'valid',
        firstAid: 'valid',
        firstAidExpiryDate: '2027-03-15',
      }),
    },
    {
      id: 'teacher-2',
      firstName: 'Michael',
      lastName: 'Johnson',
      email: 'michael.johnson@kindergarten.com',
      status: 'active',
      pinHash: hashPin('2222'),
      hireDate: '2021-01-15',
      mfa: createDefaultMfaProfile({
        qualification: 'qualified',
        echelonCode: 'E2',
        ancienneteHours: 800,
        accumulatedHours: 280,
        baseRateDisplay: 22,
      }),
      compliance: createDefaultCompliance({
        policeCheck: 'valid',
        firstAid: 'expiring',
        firstAidExpiryDate: '2026-07-01',
      }),
    },
    {
      id: 'teacher-3',
      firstName: 'Sophie',
      lastName: 'Tremblay',
      email: 'sophie.tremblay@kindergarten.com',
      status: 'active',
      pinHash: hashPin('3333'),
      hireDate: '2019-08-20',
      mfa: createDefaultMfaProfile({
        qualification: 'qualified',
        echelonCode: 'E4',
        ancienneteHours: 1650,
        accumulatedHours: 410,
        baseRateDisplay: 26,
      }),
      compliance: createDefaultCompliance({
        policeCheck: 'valid',
        firstAid: 'valid',
        firstAidExpiryDate: '2028-01-10',
      }),
    },
    {
      id: 'teacher-4',
      firstName: 'Jean',
      lastName: 'Dupont',
      email: 'jean.dupont@kindergarten.com',
      status: 'active',
      pinHash: hashPin('4444'),
      hireDate: '2023-03-01',
      mfa: createDefaultMfaProfile({
        qualification: 'non-qualified',
        echelonCode: null,
        ancienneteHours: 200,
        accumulatedHours: 150,
        baseRateDisplay: 18.5,
      }),
      compliance: createDefaultCompliance({ policeCheck: 'valid', firstAid: 'missing' }),
    },
    {
      id: 'teacher-5',
      firstName: 'Maria',
      lastName: 'Santos',
      email: 'maria.santos@kindergarten.com',
      status: 'active',
      pinHash: hashPin('5555'),
      hireDate: '2022-06-01',
      mfa: createDefaultMfaProfile({
        qualification: 'qualified',
        echelonCode: 'E2',
        ancienneteHours: 450,
        accumulatedHours: 190,
        baseRateDisplay: 22,
      }),
      compliance: createDefaultCompliance({
        policeCheck: 'expiring',
        firstAid: 'valid',
        firstAidExpiryDate: '2026-12-01',
      }),
    },
    {
      id: 'teacher-6',
      firstName: 'Lucie',
      lastName: 'Gagnon',
      email: 'lucie.gagnon@kindergarten.com',
      status: 'active',
      pinHash: hashPin('6666'),
      hireDate: '2024-01-10',
      mfa: createDefaultMfaProfile({
        qualification: 'non-qualified',
        echelonCode: null,
        ancienneteHours: 100,
        accumulatedHours: 80,
        baseRateDisplay: 17.75,
      }),
      compliance: createDefaultCompliance({ policeCheck: 'missing', firstAid: 'missing' }),
    },
    {
      id: 'teacher-7',
      firstName: 'Ahmed',
      lastName: 'Hassan',
      email: 'ahmed.hassan@kindergarten.com',
      status: 'active',
      pinHash: hashPin('7777'),
      hireDate: '2021-09-01',
      mfa: createDefaultMfaProfile({
        qualification: 'qualified',
        echelonCode: 'E3',
        ancienneteHours: 950,
        accumulatedHours: 340,
        baseRateDisplay: 24.5,
      }),
      compliance: createDefaultCompliance({
        policeCheck: 'valid',
        firstAid: 'valid',
        firstAidExpiryDate: '2027-06-20',
      }),
    },
    {
      id: 'teacher-8',
      firstName: 'Chloé',
      lastName: 'Martin',
      email: 'chloe.martin@kindergarten.com',
      status: 'active',
      pinHash: hashPin('8888'),
      hireDate: '2024-08-15',
      mfa: createDefaultMfaProfile({
        qualification: 'qualified',
        echelonCode: 'E1',
        ancienneteHours: 300,
        accumulatedHours: 60,
        baseRateDisplay: 20,
      }),
      compliance: createDefaultCompliance({
        policeCheck: 'valid',
        firstAid: 'valid',
        firstAidExpiryDate: '2028-04-01',
      }),
    },
  ] as Employee[];

  return seeds.map((employee) => ({
    ...employee,
    portfolio: employee.portfolio ?? createDefaultPortfolio(),
    documents: employee.documents ?? createDefaultDocuments(employee.compliance),
  }));
}

function normalizeEmployee(value: unknown): Employee | null {
  if (!value || typeof value !== 'object') return null;
  const e = value as Partial<Employee>;
  if (
    typeof e.id !== 'string' ||
    typeof e.firstName !== 'string' ||
    !e.firstName ||
    typeof e.lastName !== 'string' ||
    !e.lastName ||
    (e.status !== 'active' && e.status !== 'inactive') ||
    typeof e.pinHash !== 'string'
  ) {
    return null;
  }

  return {
    id: e.id,
    firstName: e.firstName,
    lastName: e.lastName,
    email: e.email,
    status: e.status,
    pinHash: e.pinHash,
    hireDate: e.hireDate,
    mfa: e.mfa
      ? { ...createDefaultMfaProfile(), ...e.mfa }
      : createDefaultMfaProfile(),
    compliance: e.compliance
      ? { ...createDefaultCompliance(), ...e.compliance }
      : createDefaultCompliance(),
    portfolio: e.portfolio
      ? { ...createDefaultPortfolio(), ...e.portfolio }
      : createDefaultPortfolio(),
    documents: e.documents?.length
      ? e.documents
      : createDefaultDocuments(
          e.compliance
            ? { ...createDefaultCompliance(), ...e.compliance }
            : createDefaultCompliance()
        ),
    inviteSent: Boolean(e.inviteSent),
    centreId: e.centreId ?? getActiveCentreId(),
  };
}

function mergeSeedEmployees(existing: Employee[]): Employee[] {
  const byId = new Map(existing.map((e) => [e.id, e]));
  for (const seed of buildSeedEmployees()) {
    if (!byId.has(seed.id)) {
      byId.set(seed.id, seed);
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.id.localeCompare(b.id));
}

export function loadEmployees(): Employee[] {
  if (typeof window === 'undefined') return buildSeedEmployees();
  try {
    const raw = localStorage.getItem(EMPLOYEE_STORAGE_KEY);
    if (!raw) {
      const seeds = buildSeedEmployees();
      saveEmployees(seeds);
      return seeds;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      const seeds = buildSeedEmployees();
      saveEmployees(seeds);
      return seeds;
    }
    const normalized = parsed
      .map(normalizeEmployee)
      .filter((e): e is Employee => e !== null);
    if (normalized.length === 0) {
      const seeds = buildSeedEmployees();
      saveEmployees(seeds);
      return seeds;
    }
    const merged = mergeSeedEmployees(normalized);
    if (merged.length !== normalized.length) {
      saveEmployees(merged);
    }
    return merged;
  } catch {
    const seeds = buildSeedEmployees();
    saveEmployees(seeds);
    return seeds;
  }
}

export function repairEmployeesStorageIfNeeded(employees: Employee[]): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(EMPLOYEE_STORAGE_KEY);
    if (!raw) {
      saveEmployees(employees);
      return;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      !Array.isArray(parsed) ||
      parsed.length === 0 ||
      parsed.map(normalizeEmployee).filter(Boolean).length === 0
    ) {
      saveEmployees(employees);
    }
  } catch {
    saveEmployees(employees);
  }
}

export function saveEmployees(employees: Employee[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(EMPLOYEE_STORAGE_KEY, JSON.stringify(employees));
    window.dispatchEvent(new CustomEvent('garderie:employees-updated'));
  } catch {
    // ignore
  }
}

export function subscribeToEmployeeUpdates(onUpdate: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => onUpdate();
  window.addEventListener('garderie:employees-updated', handler);
  return () => window.removeEventListener('garderie:employees-updated', handler);
}
